// NewWorkspaceUploader.js — the composer at the top of /workspace.
// One bar takes a YouTube link (typed, or pasted anywhere on the page) or a local video
// (picked, or dropped anywhere on the page). The workspace doc is only created once a job starts.

import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLink,
  faCircleNotch,
  faArrowUpFromBracket,
  faCloudArrowUp,
  faTriangleExclamation,
  faXmark,
  faVideo,
} from '@fortawesome/free-solid-svg-icons';
import { deleteObject } from 'firebase/storage';

// Firebase
import { auth, db } from '../../firebase';
import { doc, onSnapshot } from 'firebase/firestore';

// Business logic
import { createWorkspace } from './createWorkspace';
import { getYoutubeWORKSPACEcaptions } from '../../myStudio/myBumpUp/myCreator/getVideoCaptions';
import { uploadlocalWorkspace } from './workspaceUploads/Localworkspaceupload/uploadlocalWorkspace';
import { extractYouTubeVideoId } from './youtubeLinks';
import { track } from '../../analytics/track';
import { takePendingLink, TOOL_HINTS } from '../pendingLink';

// Shared components
import InsufficientTimePopUp from '../../components/miscComponents/InsufficientTimePopUp';
import PlanPurchasePage from '../../myPlans/PlanPurchasePage';
import LinksNotification from '../../components/appComponents/linksNotification';

// Assets
import placeholder_profile from '../../assets/images/profile/placeholder-profile.jpg';
import processBumpupslogo from '../../assets/gifmedia/bumpups-2.gif';

import './newWorkspaceUploader.css';

// ─── Constants ───────────────────────────────────────────────────────────────

const MAX_FILE_SIZE_BYTES = 16 * 1024 ** 3;

// A free account's one upload preview covers a video's first 30 minutes (all of it when shorter), for
// videos up to Pro's 3 hours 30 minutes. Mirrors PREVIEW_MAX_SECONDS, PREVIEW_SOURCE_MAX_SECONDS and the
// tolerance in the collector's helper_functions/preview_upload.py
export const PREVIEW_MAX_SECONDS = 30 * 60 + 5;
export const PREVIEW_SOURCE_MAX_SECONDS = 3 * 3600 + 30 * 60 + 5;

// How long to wait for the backend to pick up a job before giving up on a clean hand-off
const JOB_START_TIMEOUT_MS = 30000;

// Matches the title field limit on the workspace page
const MAX_TITLE_LENGTH = 100;

// ─── Helpers ─────────────────────────────────────────────────────────────────

const generateClientId = () => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from({ length: 16 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
};

const generateWorkspaceName = () => {
  const now = new Date();
  const date = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const time = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `Workspace ${date}, ${time}`;
};

// Workspaces are named after their video; the timestamp name is only a fallback
const workspaceNameFor = videoTitle => {
  const clean = (videoTitle || '').replace(/\s+/g, ' ').trim();
  if (!clean) return generateWorkspaceName();
  return clean.length > MAX_TITLE_LENGTH ? `${clean.slice(0, MAX_TITLE_LENGTH - 1).trimEnd()}…` : clean;
};

const fileBaseName = name => (name || '').replace(/\.[^./\\]+$/, '');

// Same rounding the backend uses when it deducts minutes
const minutesCharged = seconds => Math.max(Math.floor(seconds / 60), 1);

const convertDurationToSeconds = iso => {
  if (!iso) return 0;
  const m = iso.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
  if (!m) return 0;
  return (m[1] ? parseInt(m[1]) : 0) * 3600
    + (m[2] ? parseInt(m[2]) : 0) * 60
    + (m[3] ? parseInt(m[3]) : 0);
};

const fmtDuration = s => {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${m}:${String(sec).padStart(2, '0')}`;
};

const isEditableTarget = el =>
  !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));

// ─── Main Component ───────────────────────────────────────────────────────────

const NewWorkspaceUploader = forwardRef(({ onBusyChange }, ref) => {
  const user = auth.currentUser;
  const navigate = useNavigate();

  // ── Plan & Credits ────────────────────────────────────────────────────────
  const [currentPlan, setCurrentPlan] = useState('free');
  const [creditsUsed, setCreditsUsed]  = useState(0);
  const [creditsLimit, setCreditsLimit] = useState(0);
  const [previewUploadsUsed, setPreviewUploadsUsed] = useState(0);
  const [isPlanLoaded, setIsPlanLoaded] = useState(false);

  useEffect(() => {
    if (!user) return;
    return onSnapshot(doc(db, 'users', user.uid, 'subscriptionData', 'planDetails'), s => {
      if (!s.exists()) return;
      const d = s.data();
      setCurrentPlan((d.currentPlan || 'free').toLowerCase());
      setCreditsUsed(d.creditsUsed ?? 0);
      setCreditsLimit(d.creditsLimit ?? 0);
      setPreviewUploadsUsed(d.previewUploadsUsed ?? 0);
      setIsPlanLoaded(true);
    }, () => {});
  }, [user]);

  const isPro = currentPlan === 'pro';
  const remainingCredits = Math.max(creditsLimit - creditsUsed, 0);
  // Free accounts get one upload as a free preview (first chapters + a few messages until Pro)
  const canPreview = isPlanLoaded && !isPro && previewUploadsUsed < 1;
  const canUpload = (isPro && remainingCredits > 0) || canPreview;

  // ── Workspace creation ────────────────────────────────────────────────────
  // Retrying the same YouTube video reuses the workspace created for it
  const pendingWorkspaceRef = useRef(null); // { videoId, id }

  const ensureWorkspace = useCallback(async (videoId, videoTitle) => {
    if (pendingWorkspaceRef.current?.videoId === videoId) return pendingWorkspaceRef.current.id;
    const fullId = await createWorkspace({ user, workspaceTitle: workspaceNameFor(videoTitle) });
    const id = fullId.replace(/^workspace-/, '');
    pendingWorkspaceRef.current = { videoId, id };
    localStorage.removeItem(`videoCache_${user.uid}_WorkspaceVideos`);
    return id;
  }, [user]);

  // ── YouTube state ─────────────────────────────────────────────────────────
  // ytStatus: 'idle' | 'searching' | 'ready' | 'uploading' | 'error'
  const [ytUrl,     setYtUrl]     = useState('');
  const [ytDetails, setYtDetails] = useState(null);
  const [ytStatus,  setYtStatus]  = useState('idle');
  const [ytError,   setYtError]   = useState(null); // 'not_found' | 'too_long' | 'live' | 'failed'
  // A picked or dropped file that can't be used: 'too_big' | 'unreadable'
  const [fileError, setFileError] = useState(null);
  const urlInputRef = useRef(null);

  // Leaving /workspace mid-job must not yank the user back to the new workspace later:
  // on unmount stop any job watcher, and don't start one if createWorkspace resolves afterwards.
  const isMountedRef = useRef(false);
  const stopJobWatchRef = useRef(null);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      stopJobWatchRef.current?.();
    };
  }, []);

  // Waits until the backend has picked up the job for a workspace, so we only redirect once the
  // workspace page can render the real processing state instead of an empty draft.
  const watchForJobStart = useCallback((cid, hasStarted, onStarted, onTimeout) => {
    const wsRef = doc(db, 'users', user.uid, 'workspaceContent', `workspace-${cid}`);
    let done = false;
    let unsub = () => {};
    let timer = null;
    const stop = () => {
      done = true;
      unsub();
      clearTimeout(timer);
      stopJobWatchRef.current = null;
    };
    stopJobWatchRef.current = stop;

    unsub = onSnapshot(wsRef, snap => {
      if (done || !snap.exists() || !hasStarted(snap.data())) return;
      stop();
      localStorage.removeItem(`videoCache_${user.uid}_WorkspaceVideos`);
      onStarted();
    });
    timer = setTimeout(() => {
      if (!done) {
        stop();
        onTimeout();
      }
    }, JOB_START_TIMEOUT_MS);
  }, [user]);

  // Fetch YouTube video metadata
  // Each lookup gets a number; a response for anything but the latest link (the box was cleared or
  // a different link pasted meanwhile) is ignored
  const lookupRef = useRef(0);

  const fetchYTDetails = useCallback(async url => {
    const lookup = ++lookupRef.current;
    const videoId = extractYouTubeVideoId(url);
    if (!videoId) {
      setYtStatus('error');
      setYtError('not_found');
      setYtDetails(null);
      return;
    }

    setYtStatus('searching');
    setYtDetails(null);
    setYtError(null);

    try {
      const { data } = await axios.get('https://www.googleapis.com/youtube/v3/videos', {
        params: {
          part: 'snippet,contentDetails,status',
          id: videoId,
          key: process.env.REACT_APP_YOUTUBE_API_KEY,
        },
      });
      if (lookup !== lookupRef.current) return;

      if (!data.items?.length) {
        setYtStatus('error'); setYtError('not_found'); return;
      }

      const { snippet, contentDetails, status } = data.items[0];
      const durationSeconds = convertDurationToSeconds(contentDetails.duration);

      // Live streams, premieres, and just-ended streams still being processed come
      // back with liveBroadcastContent != 'none' and/or a zero (P0D) duration. Gemini
      // cannot ingest them, so block here instead of charging the user and failing.
      const isLiveOrProcessing =
        (snippet.liveBroadcastContent && snippet.liveBroadcastContent !== 'none') || durationSeconds <= 0;
      if (isLiveOrProcessing) {
        setYtStatus('error'); setYtError('live'); return;
      }

      if (durationSeconds > 10800) {
        setYtStatus('error'); setYtError('too_long'); return;
      }

      const thumbnailUrl =
        snippet.thumbnails?.maxres?.url ||
        snippet.thumbnails?.standard?.url ||
        snippet.thumbnails?.high?.url;

      if (!thumbnailUrl) {
        setYtStatus('error'); setYtError('not_found'); return;
      }

      setYtDetails({ videoId, title: snippet.title, thumbnailUrl, durationSeconds, visibility: status.privacyStatus });
      setYtStatus('ready');
    } catch {
      if (lookup !== lookupRef.current) return;
      setYtStatus('error');
      setYtError('not_found');
    }
  }, []);

  const applyUrl = useCallback(url => {
    setYtUrl(url);
    setFileError(null);
    if (!url.trim()) {
      lookupRef.current++; // drop any lookup still in flight
      setYtStatus('idle'); setYtDetails(null); setYtError(null); return;
    }
    if (extractYouTubeVideoId(url)) {
      fetchYTDetails(url);
    } else {
      lookupRef.current++;
      setYtStatus('error');
      setYtError('not_found');
      setYtDetails(null);
    }
  }, [fetchYTDetails]);

  const handleUrlChange = useCallback(e => applyUrl(e.target.value), [applyUrl]);

  // A link pasted into a free tool page before signing up is waiting here, with what to do next
  const [toolHint, setToolHint] = useState(null);
  useEffect(() => {
    const pending = takePendingLink();
    if (!pending) return;
    applyUrl(pending.url);
    setToolHint(TOOL_HINTS[pending.tool] || null);
    track('tool_handoff', { tool: pending.tool || 'unknown' });
  }, [applyUrl]);

  const isOverTimebank = !!(ytDetails && ytDetails.durationSeconds > remainingCredits * 60);

  const handleYTUpload = useCallback(async () => {
    if (!ytDetails?.videoId) return;
    setYtStatus('uploading');
    track('video_started', { kind: 'youtube', minutes: minutesCharged(ytDetails.durationSeconds) });
    try {
      const cid = await ensureWorkspace(ytDetails.videoId, ytDetails.title);

      // Fire the processing job — don't await; redirect is Firestore-driven
      getYoutubeWORKSPACEcaptions({
        videoId: ytDetails.videoId,
        durationInSeconds: ytDetails.durationSeconds,
        thumbnailUrl: ytDetails.thumbnailUrl,
        title: ytDetails.title,
        fullUrl: ytUrl,
        content_id: cid,
      }).catch(() => {});

      // User already left /workspace — the job keeps running and shows up in the list
      if (!isMountedRef.current) return;

      watchForJobStart(
        cid,
        // Long videos (≥30 min): backend sets is_caption_processing: true early
        // Short videos (<30 min): Gemini finishes immediately, sets is_transcript_available: true directly
        d => d.is_caption_processing || d.is_transcript_available,
        () => navigate(`/workspace/${cid}`),
        () => { setYtStatus('error'); setYtError('failed'); }
      );
    } catch {
      setYtStatus('error');
      setYtError('failed');
    }
  }, [ytDetails, ytUrl, ensureWorkspace, watchForJobStart, navigate]);

  // ── Local Upload state ────────────────────────────────────────────────────
  const [localFile,          setLocalFile]          = useState(null);
  const [localProgress,      setLocalProgress]      = useState(0);
  const [showUploadModal,    setShowUploadModal]     = useState(false);
  const [videoDurationSec,   setVideoDurationSec]   = useState(null);
  const [showInsufficientTime, setShowInsufficientTime] = useState(false);
  const [showPlanPopUp,      setShowPlanPopUp]      = useState(false);
  const [localContentId,     setLocalContentId]     = useState(null);
  const [isDragging,         setIsDragging]         = useState(false);
  const [previewTooLong,     setPreviewTooLong]     = useState(false);
  // A free account's video over 30 minutes: { file, duration } while it decides on a preview of the first 30
  const [trimOffer,          setTrimOffer]          = useState(null);
  const [isTrimmedPreview,   setIsTrimmedPreview]   = useState(false);
  const [plansSource,        setPlansSource]        = useState('upload_button');

  const fileInputRef   = useRef(null);
  const uploadTaskRef  = useRef(null);
  const storagePathRef = useRef('');

  // Uploads the file and fires its processing job. trimmed: a free preview of a longer video's first
  // 30 minutes (the collector cuts it; the whole file is still uploaded and kept)
  const startUpload = useCallback((file, duration, trimmed) => {
    track('video_started', trimmed
      ? { kind: 'preview', minutes: 30, trimmed: true }
      : { kind: isPro ? 'upload' : 'preview', minutes: minutesCharged(duration) });
    setIsTrimmedPreview(trimmed);

    // Generate client-side ID for storage path — no Firestore write yet
    const tempId = generateClientId();
    setLocalFile(file);
    setLocalContentId(tempId);

    uploadlocalWorkspace(
      file, user, tempId,
      setLocalProgress,
      setShowUploadModal,
      storagePathRef,
      uploadTaskRef,
      // Upload done and job fired: keep the modal on "Finalizing…" until the backend has
      // picked the job up, then open the workspace straight into its processing state.
      () => {
        if (!isMountedRef.current) return;
        const openWorkspace = () => navigate(`/workspace/${tempId}`);
        watchForJobStart(
          tempId,
          d => d.is_local_caption_processing || d.is_local_transcript_available || d.is_transcript_error,
          openWorkspace,
          openWorkspace
        );
      },
      // Create workspace in Firestore only after upload succeeds
      async (id) => {
        const fullId = await createWorkspace({ user, workspaceTitle: workspaceNameFor(fileBaseName(file.name)), workspaceId: id });
        const cid = fullId.replace(/^workspace-/, '');
        localStorage.removeItem(`videoCache_${user.uid}_WorkspaceVideos`);
        return cid;
      }
    );
  }, [isPro, user, navigate, watchForJobStart]);

  const handleLocalFile = useCallback(async file => {
    if (!file) return;
    setFileError(null);
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setFileError('too_big');
      return;
    }

    const videoEl = document.createElement('video');
    videoEl.preload = 'metadata';
    // Not a video the browser can read (a document, an MKV, a damaged file): say so instead of doing nothing
    videoEl.onerror = () => {
      window.URL.revokeObjectURL(videoEl.src);
      setFileError('unreadable');
    };
    videoEl.onloadedmetadata = () => {
      window.URL.revokeObjectURL(videoEl.src);
      const duration = videoEl.duration;
      if (!(duration > 0) || !videoEl.videoWidth) {
        setFileError('unreadable');
        return;
      }
      setVideoDurationSec(duration);

      if (!isPro) {
        if (duration > PREVIEW_SOURCE_MAX_SECONDS) {
          track('upload_blocked', { reason: 'preview_too_long', minutes: minutesCharged(duration) });
          setPreviewTooLong(true);
          return;
        }
        if (duration > PREVIEW_MAX_SECONDS) {
          track('preview_trim_offered', { minutes: minutesCharged(duration) });
          setTrimOffer({ file, duration });
          return;
        }
      } else if (duration > remainingCredits * 60) {
        setShowInsufficientTime(true);
        return;
      }
      startUpload(file, duration, false);
    };
    videoEl.src = URL.createObjectURL(file);
  }, [isPro, remainingCredits, startUpload]);

  const handleFileInputChange = useCallback(e => {
    const file = e.target.files[0];
    if (file) handleLocalFile(file);
    e.target.value = null;
  }, [handleLocalFile]);

  // ── Derived flags ─────────────────────────────────────────────────────────
  const isSearching  = ytStatus === 'searching';
  const isUploading  = ytStatus === 'uploading';
  const isFailed     = ytStatus === 'error' && ytError === 'failed';
  const hasPreview   = !!ytDetails && ytStatus !== 'idle' && !isFailed;
  // With a link in the box the YouTube flow is the only action: Upload video gives way to a clear button
  const hasLink      = ytUrl.trim().length > 0;
  // The note after "N minutes left": upload notes (Pro needed, file formats) only while no link is in
  // the box; running out of minutes matters either way
  const hintNote = remainingCredits === 0 && (isPro || hasLink) ? 'out'
    : hasLink ? null
    : canPreview ? 'preview'
    : !isPro ? 'pro'
    : 'formats';

  // While a workspace is being created, the page holds the list so the new row doesn't
  // appear before we redirect into it.
  const isBusy = isUploading || showUploadModal;
  useEffect(() => {
    onBusyChange?.(isBusy);
  }, [isBusy, onBusyChange]);

  // Pro uploads, or a free account's one preview; everyone else gets the plan popup instead of a file picker
  const handleUploadClick = () => {
    if (!canUpload) {
      track('upload_blocked', { reason: isPro ? 'out_of_minutes' : 'preview_used' });
      setPlansSource('upload_button');
      setShowPlanPopUp(true);
      return;
    }
    fileInputRef.current?.click();
  };

  // The getting-started panel on /workspace drives these (inside its own click, so the file picker opens)
  useImperativeHandle(ref, () => ({
    openUpload: handleUploadClick,
    focusLink: () => {
      urlInputRef.current?.focus();
      urlInputRef.current?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    },
  }));

  // ── Page-wide paste: a YouTube link pasted anywhere lands in the bar ──────
  useEffect(() => {
    const onPaste = e => {
      if (isEditableTarget(e.target) || isBusy) return;
      const text = e.clipboardData?.getData('text')?.trim();
      if (!text || !extractYouTubeVideoId(text)) return;
      e.preventDefault();
      applyUrl(text);
      urlInputRef.current?.focus();
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [applyUrl, isBusy]);

  // ── Page-wide drag-and-drop: files dropped anywhere on /workspace upload here ──
  // The window listeners also keep a stray drop from navigating the tab to the file.
  useEffect(() => {
    const hasFiles = e => Array.from(e.dataTransfer?.types || []).includes('Files');
    const onDragEnter = e => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      if (!isBusy) setIsDragging(true);
    };
    const onDragOver = e => { if (hasFiles(e)) e.preventDefault(); };
    const onDrop = e => {
      if (!hasFiles(e)) return;
      e.preventDefault();
      setIsDragging(false);
    };
    window.addEventListener('dragenter', onDragEnter);
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragenter', onDragEnter);
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('drop', onDrop);
    };
  }, [isBusy]);

  const handleOverlayDrop = e => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (!file || isBusy) return;
    if (!canUpload) {
      track('upload_blocked', { reason: isPro ? 'out_of_minutes' : 'preview_used' });
      setPlansSource('upload_button');
      setShowPlanPopUp(true);
      return;
    }
    handleLocalFile(file);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const costMinutes = ytDetails ? minutesCharged(ytDetails.durationSeconds) : 0;

  return (
    <div className="nwp-uploader">
      {/* Notification banner */}
      <div className="nwp-notification-wrap">
        <LinksNotification />
      </div>

      <div className={`nwp-composer${isUploading ? ' nwp-composer--locked' : ''}`}>
        <div className="nwp-composer-row">
          <div className="nwp-input-icon">
            {isSearching || isUploading
              ? <FontAwesomeIcon icon={faCircleNotch} spin className="nwp-icon-spin" />
              : <FontAwesomeIcon icon={faLink} className="nwp-icon-link" />
            }
          </div>
          <input
            ref={urlInputRef}
            type="text"
            className="nwp-url-input"
            placeholder="Paste a YouTube link, or drop a video anywhere…"
            value={ytUrl}
            onChange={handleUrlChange}
            disabled={isUploading}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            aria-label="YouTube link"
          />
          {isUploading ? (
            <div className="nwp-input-processing-badge">Starting…</div>
          ) : hasLink ? (
            <button
              type="button"
              className="nwp-clear-btn"
              onClick={() => { applyUrl(''); urlInputRef.current?.focus(); }}
              aria-label="Clear link"
              title="Clear link"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          ) : (
            <button type="button" className="nwp-upload-btn" onClick={handleUploadClick}>
              <FontAwesomeIcon icon={faArrowUpFromBracket} />
              <span className="nwp-upload-btn-label">Upload video</span>
              {isPlanLoaded && !isPro && (canPreview
                ? <span className="nwp-pro-tag nwp-pro-tag--free">TRY FREE</span>
                : <span className="nwp-pro-tag">PRO</span>)}
            </button>
          )}
        </div>

        {/* Inline preview of the pasted video */}
        {hasPreview && (
          <div className="nwp-preview">
            <a
              href={`https://youtu.be/${ytDetails.videoId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="nwp-thumbnail-link"
            >
              <img src={ytDetails.thumbnailUrl} alt="Video thumbnail" className="nwp-thumbnail" />
            </a>
            <div className="nwp-video-info">
              <p className="nwp-video-title" title={ytDetails.title}>{ytDetails.title}</p>
              <p className="nwp-video-meta">
                {fmtDuration(ytDetails.durationSeconds)}
                <span className="nwp-meta-dot">·</span>
                {ytDetails.visibility === 'public' ? 'Public' : 'Unlisted'}
              </p>
              <p className={`nwp-video-cost${isOverTimebank ? ' nwp-video-cost--short' : ''}`}>
                {isOverTimebank
                  ? `Needs ${costMinutes.toLocaleString()} min · you have ${remainingCredits.toLocaleString()}`
                  : `Uses ${costMinutes.toLocaleString()} of your ${remainingCredits.toLocaleString()} minutes`}
              </p>
            </div>
            {ytStatus === 'ready' && (
              <div className="nwp-action-row">
                {isOverTimebank ? (
                  <Link to="/settings/plan" className="nwp-btn nwp-btn-upgrade">Get more minutes</Link>
                ) : (
                  <button className="nwp-btn nwp-btn-primary" onClick={handleYTUpload}>Start</button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Error: not found / too long / live or still processing */}
        {ytStatus === 'error' && (ytError === 'not_found' || ytError === 'too_long' || ytError === 'live') && ytUrl && (
          <div className="nwp-url-error-bar">
            {ytError === 'too_long'
              ? 'Max duration is 3 hours'
              : ytError === 'live'
              ? 'Live streams and videos still processing aren’t supported yet'
              : 'Video not found — check the URL'}
          </div>
        )}

        {/* Error: the picked or dropped file can't be used */}
        {fileError && !hasLink && (
          <div className="nwp-url-error-bar">
            {fileError === 'too_big'
              ? 'That file is over 16 GB. Choose a smaller video.'
              : 'Couldn’t read that file. Use an MP4, MOV or WebM video.'}
          </div>
        )}

        {/* Error: processing failed */}
        {isFailed && (
          <div className="nwp-url-error-bar nwp-url-error-bar--failed">
            <FontAwesomeIcon icon={faTriangleExclamation} className="nwp-error-bar-icon" />
            <span>Processing failed</span>
            <button
              className="nwp-error-retry-btn"
              onClick={() => { setYtStatus('idle'); setYtError(null); setYtUrl(''); setYtDetails(null); }}
            >
              Try again
            </button>
          </div>
        )}
      </div>

      {toolHint && hasLink && <div className="nwp-tool-hint">{toolHint}</div>}

      {/* Plan / minutes hint */}
      {isPlanLoaded && (
        <div className="nwp-hint">
          <span>{remainingCredits.toLocaleString()} minutes left</span>
          {hintNote && <span className="nwp-meta-dot">·</span>}
          {hintNote === 'preview' && (
            <span>Your first upload is free to preview (its first 30 minutes)</span>
          )}
          {hintNote === 'pro' && (
            <span>
              Uploading your own videos needs Pro{' '}
              <button type="button" className="nwp-hint-link" onClick={() => { setPlansSource('upload_hint'); setShowPlanPopUp(true); }}>View plans</button>
            </span>
          )}
          {hintNote === 'out' && (
            <span>
              Out of minutes until your plan renews{' '}
              <button type="button" className="nwp-hint-link" onClick={() => setShowPlanPopUp(true)}>View plan</button>
            </span>
          )}
          {hintNote === 'formats' && <span>MP4, MOV or WebM up to 16 GB</span>}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="video/mp4,video/mov,video/webm,video/quicktime"
        onChange={handleFileInputChange}
        hidden
        disabled={!canUpload}
      />

      {/* Full-page drop target while a file is dragged over the window */}
      {isDragging && (
        <div
          className="nwp-drop-overlay"
          onDragOver={e => e.preventDefault()}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleOverlayDrop}
        >
          <div className="nwp-drop-overlay-card">
            <FontAwesomeIcon icon={faCloudArrowUp} className="nwp-drop-overlay-icon" />
            <p className="nwp-drop-overlay-title">
              {canPreview ? 'Drop your video for a free preview' : canUpload ? 'Drop your video to upload' : 'Uploading your own videos needs Pro'}
            </p>
            <p className="nwp-drop-overlay-sub">
              {canPreview ? 'MP4, MOV or WebM. The preview covers the first 30 minutes' : canUpload ? 'MP4, MOV or WebM up to 16 GB' : 'Drop it anyway to see plans'}
            </p>
          </div>
        </div>
      )}

      {/* ── Upload modal (local video) ──────────────────────────────────── */}
      {showUploadModal && localFile && (
        <UploadModal
          file={localFile}
          progress={localProgress}
          uploadTaskRef={uploadTaskRef}
          storagePathRef={storagePathRef}
          videoDurationSec={videoDurationSec}
          remainingCredits={remainingCredits}
          isPreview={!isPro}
          isTrimmedPreview={isTrimmedPreview}
          onClose={() => {
            setShowUploadModal(false);
            if (localContentId) navigate(`/workspace/${localContentId}`);
          }}
          onDiscard={() => {
            setShowUploadModal(false);
            setLocalContentId(null);
            setLocalFile(null);
          }}
        />
      )}

      {showInsufficientTime && (
        <InsufficientTimePopUp
          onClose={() => setShowInsufficientTime(false)}
          neededMinutes={minutesCharged(videoDurationSec || 0)}
          remainingMinutes={remainingCredits}
        />
      )}

      {previewTooLong && (
        <div className="nwp-confirm-overlay" onClick={() => setPreviewTooLong(false)}>
          <div className="nwp-confirm-box" role="dialog" aria-modal="true" aria-labelledby="nwp-ptl-title" onClick={e => e.stopPropagation()}>
            <h3 className="nwp-confirm-title" id="nwp-ptl-title">This video is too long</h3>
            <p className="nwp-confirm-body">
              This video is {fmtDuration(Math.round(videoDurationSec || 0))} long. Videos can be up to 3 hours 30 minutes,
              on the free preview and on Pro.
            </p>
            <div className="nwp-confirm-actions">
              <button className="nwp-confirm-btn nwp-confirm-btn--pro" onClick={() => setPreviewTooLong(false)}>
                Choose another
              </button>
            </div>
          </div>
        </div>
      )}

      {trimOffer && (
        <div className="nwp-confirm-overlay" onClick={() => setTrimOffer(null)}>
          <div className="nwp-confirm-box" role="dialog" aria-modal="true" aria-labelledby="nwp-trim-title" onClick={e => e.stopPropagation()}>
            <h3 className="nwp-confirm-title" id="nwp-trim-title">Preview the first 30 minutes free</h3>
            <p className="nwp-confirm-body">
              This video is {fmtDuration(Math.round(trimOffer.duration))} long. Your free preview covers its first 30 minutes,
              with the first chapters and 2 chat answers. Pro processes the whole video.
            </p>
            <div className="nwp-confirm-actions">
              <button
                className="nwp-confirm-btn nwp-confirm-btn--secondary"
                onClick={() => { setTrimOffer(null); setPlansSource('preview_trim'); setShowPlanPopUp(true); }}
              >
                See Pro plans
              </button>
              <button
                className="nwp-confirm-btn nwp-confirm-btn--pro"
                onClick={() => { const { file, duration } = trimOffer; setTrimOffer(null); startUpload(file, duration, true); }}
              >
                Preview free
              </button>
            </div>
          </div>
        </div>
      )}

      {showPlanPopUp && (
        <PlanPurchasePage
          source={plansSource}
          onClose={() => setShowPlanPopUp(false)}
        />
      )}
    </div>
  );
});

export default NewWorkspaceUploader;

// ─── Upload Modal ─────────────────────────────────────────────────────────────

function UploadModal({
  file, progress, uploadTaskRef, storagePathRef,
  videoDurationSec, remainingCredits, isPreview, isTrimmedPreview, onClose, onDiscard,
}) {
  const user = auth.currentUser;

  const [videoUrl,      setVideoUrl]      = useState('');
  const [statusText,    setStatusText]    = useState('');
  const [profileImage,  setProfileImage]  = useState(placeholder_profile);
  const [showConfirm,   setShowConfirm]   = useState(false);
  const [isDiscarding,  setIsDiscarding]  = useState(false);
  const [uploadError,   setUploadError]   = useState(false);
  const [isOutOfTime,   setIsOutOfTime]   = useState(false);

  // Object URL for video preview
  useEffect(() => {
    const url = URL.createObjectURL(file);
    setVideoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Status text
  useEffect(() => {
    if (progress < 98)       setStatusText(`Uploading ${progress}%`);
    else if (progress < 100) setStatusText('Uploading 98%');
    else                     setStatusText('Finalizing…');
  }, [progress]);

  // Out-of-time check (a free preview uses no minutes)
  useEffect(() => {
    if (!isPreview && remainingCredits * 60 < (videoDurationSec || 0) && progress < 100) {
      setIsOutOfTime(true);
      uploadTaskRef.current?.cancel();
    } else {
      setIsOutOfTime(false);
    }
  }, [isPreview, videoDurationSec, progress, remainingCredits, uploadTaskRef]);

  // Profile image
  useEffect(() => {
    if (!user) return;
    return onSnapshot(doc(db, 'users', user.uid, 'userSettings', 'profile'), s => {
      setProfileImage(s.data()?.profileImage || placeholder_profile);
    }, () => {});
  }, [user]);

  // Watch for upload errors from the Firebase task
  useEffect(() => {
    uploadTaskRef.current?.on?.('state_changed', null, (error) => {
      if (error?.code === 'storage/canceled') return;
      setUploadError(true);
    });
  }, [uploadTaskRef]);

  // Warn before page unload during upload
  useEffect(() => {
    const handler = e => {
      if (progress < 100) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [progress]);

  const handleDiscard = async () => {
    setIsDiscarding(true);
    if (progress < 100) {
      uploadTaskRef.current?.cancel();
      if (storagePathRef.current) await deleteObject(storagePathRef.current).catch(() => {});
    }
    setShowConfirm(false);
    if (onDiscard) onDiscard();
    else onClose();
  };

  const handleClose = () => {
    if (progress >= 100) { onClose(); return; }
    // Nothing left to keep once the upload failed or was stopped for lack of minutes
    if (uploadError || isOutOfTime) { handleDiscard(); return; }
    setShowConfirm(true);
  };

  const displayedProgress = Math.min(progress, 98);
  const truncateName = name => name.length > 32 ? name.slice(0, 32) + '…' : name;

  return (
    <div className="nwp-modal-backdrop">
      <div className="nwp-modal">

        {/* Header */}
        <div className="nwp-modal-header">
          <img src={profileImage} alt="Profile" className="nwp-modal-avatar" />
          <div className="nwp-modal-header-right">
            <span className={`nwp-modal-filename${uploadError || isOutOfTime ? ' nwp-modal-filename--error' : ''}`}>
              {truncateName(file.name)}
            </span>
            <span className={`nwp-modal-status${uploadError || isOutOfTime ? ' nwp-modal-status--error' : ''}`}>
              {isOutOfTime ? 'Out of credits' : (uploadError ? 'Upload error — retry' : statusText)}
            </span>
          </div>
          <button className="nwp-modal-close" onClick={handleClose} aria-label="Close">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        {/* Progress bar */}
        <div className="nwp-modal-progress-track">
          <div
            className={`nwp-modal-progress-fill${uploadError || isOutOfTime ? ' nwp-modal-progress-fill--error' : ''}`}
            style={{ width: `${displayedProgress}%` }}
          />
        </div>

        {/* Video preview */}
        <video src={videoUrl} controls className="nwp-modal-video" />

        {/* Note */}
        <div className="nwp-modal-note">
          <FontAwesomeIcon icon={faVideo} className="nwp-modal-note-icon" />
          {isTrimmedPreview
            ? 'Free preview of the first 30 minutes: you’ll see its first chapters and 2 chat answers, and Pro unlocks the rest. Keep this tab open during upload.'
            : isPreview
              ? 'Free preview: you’ll see the first chapters and 2 chat answers, and Pro unlocks the rest. Keep this tab open during upload.'
              : 'Keep this tab open during upload. Credits are deducted at 100%.'}{' '}
          <a
            href="/help/articles/9742215-how-does-the-bump-video-upload-process-work"
            className="nwp-modal-note-link"
          >
            Learn more
          </a>
        </div>
      </div>

      {/* Discard confirmation */}
      {showConfirm && (
        <div className="nwp-confirm-overlay" onClick={() => setShowConfirm(false)}>
          <div className="nwp-confirm-box" onClick={e => e.stopPropagation()}>
            {isDiscarding ? (
              <div className="nwp-confirm-loading">
                <img src={processBumpupslogo} alt="Loading" className="nwp-confirm-gif" />
                <span className="nwp-confirm-loading-text">Deleting…</span>
              </div>
            ) : (
              <>
                <h3 className="nwp-confirm-title">Cancel upload?</h3>
                <p className="nwp-confirm-body">
                  Your video is still uploading. Discard this draft?
                </p>
                <div className="nwp-confirm-actions">
                  <button className="nwp-confirm-btn nwp-confirm-btn--keep" onClick={() => setShowConfirm(false)}>
                    Keep uploading
                  </button>
                  <button className="nwp-confirm-btn nwp-confirm-btn--discard" onClick={handleDiscard}>
                    Discard
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
